import { AssigneeActions, IAssignee, IAssigneesStoreState } from "@gtmhub/assignees";
import { getFilteredAssigneesRedux } from "@gtmhub/assignees/redux/assignee-selectors";
import { AssignPermissions } from "@gtmhub/configuration";
import { GtmhubController } from "@gtmhub/core";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { IdMap, unique } from "@gtmhub/util";
import { IController, IComponentOptions, IScope, IOnChangesObject } from "angular";

// user and team in cf is declared as "both"
export type Permissions = AssignPermissions | "both";

const bindStateToCtrl = (state: IAssigneesStoreState) => {
  return {
    assignees: state.assignees.items,
    assigneesMap: state.assignees.map,
  };
};

export interface IOnUpdateUserCfParams {
  fieldName: string;
  cf: string;
  params: {
    action: "added" | "removed";
  };
}

export class CfUserPickerCtrl extends GtmhubController implements IController {
  selectedAssignees: IAssignee[];
  assignees: IAssignee[];
  assigneesMap: IdMap<IAssignee>;
  // bindings
  fieldName: string;
  targetValue: string[];
  permissions: Permissions;
  disabled: boolean;
  onUpdate: (params: IOnUpdateUserCfParams) => void;

  static $inject = ["$scope", "$ngRedux", "AssigneeActions"];
  constructor(private $scope: IScope & IExtendedRootScopeService, $ngRedux: INgRedux, assigneeActions: AssigneeActions) {
    super();
    this.onDestroy($ngRedux.connect(bindStateToCtrl)(this));
    $ngRedux.dispatch(assigneeActions.getAssignees());
  }

  $onInit(): void {
    this.$scope
      .watchUntil(
        () => this.assignees,
        (assignees) => assignees && assignees.length
      )
      .then(() => {
        // unique is needed since there was a bug before this component which was adding the same id twice to the array
        // and now there are objects stuck with these duplicated values in their arrays so they needed to be filtered out
        this.processAssignees();
      });
  }

  $onChanges(onChangesObj: IOnChangesObject): void {
    if (onChangesObj.targetValue?.currentValue && Array.isArray(onChangesObj.targetValue.previousValue)) {
      this.processAssignees();
    }
  }

  add = (assignee: IAssignee): void => {
    this.onUpdate({ fieldName: this.fieldName, cf: assignee.id, params: { action: "added" } });
  };

  remove = (assignee: IAssignee): void => {
    this.onUpdate({ fieldName: this.fieldName, cf: assignee.id, params: { action: "removed" } });
  };

  getAssignees = (filterValue: string): IAssignee[] => {
    return getFilteredAssigneesRedux({
      assignees: this.assignees,
      assigneeName: filterValue,
      assigneeType: this.permissions !== "user and team" && this.permissions !== "both" ? this.permissions : "both",
      limit: 50,
    });
  };

  private processAssignees = (): void => {
    this.selectedAssignees = unique(this.targetValue || []).reduce(
      (assignees, assigneeId) => (this.assigneesMap[assigneeId] && assignees.push(this.assigneesMap[assigneeId]), assignees),
      []
    );
  };
}

export const CfUserPickerComponent: IComponentOptions = {
  bindings: {
    fieldName: "@",
    targetValue: "<",
    permissions: "@",
    disabled: "<",
    onUpdate: "&",
  },
  controller: CfUserPickerCtrl,
  templateUrl: require("./cf-user-picker.component.jade"),
};
